import * as React from "react";
import { Icon } from "../actions/Icon";

export type WorkspaceMenuItem =
  | {
      id: string;
      label: string;
      /** Phosphor icon name. */
      icon?: string;
      /** Second line, e.g. "4 members". */
      meta?: string;
      /** Set on workspace entries: true marks the current one. Leave undefined for actions. */
      checked?: boolean;
      divider?: false;
    }
  | { divider: true };

/**
 * The workspace card at the top of the nav rail: name, a meta line and a caret. Pass `menu` to
 * make it open a short quick menu (switch workspace, invite, settings).
 */
export interface WorkspaceSwitcherProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
  /** Workspace name. */
  name: string;
  /** Secondary line, e.g. "4 members". */
  meta?: string;
  /** Optional letter tile before the name. */
  initial?: string;
  /** Open look when there is no `menu` (the caller owns the popover). */
  open?: boolean;
  onClick?: () => void;
  /** Quick-menu items. When set, the card toggles its own menu. */
  menu?: WorkspaceMenuItem[];
  onMenuSelect?: (id: string) => void;
}

export function WorkspaceSwitcher({
  name,
  meta,
  initial,
  open,
  onClick,
  menu,
  onMenuSelect,
  className,
  ...rest
}: WorkspaceSwitcherProps): JSX.Element {
  const menuId = React.useId();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const isOpen = menu ? menuOpen : Boolean(open);

  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("[data-menu-item]") ?? []);
  const close = (refocus: boolean) => {
    setMenuOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  React.useEffect(() => {
    if (!menuOpen) return;
    items()[0]?.focus();
    const onPointer = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [menuOpen]);

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    let next = -1;
    if (e.key === "ArrowDown") next = (at + 1) % list.length;
    else if (e.key === "ArrowUp") next = (at - 1 + list.length) % list.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = list.length - 1;
    else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
      return;
    } else if (e.key === "Tab") {
      setMenuOpen(false);
      return;
    }
    if (next >= 0) {
      e.preventDefault();
      list[next]?.focus();
    }
  };

  return (
    <div ref={rootRef} className={["gk-workspace", className].filter(Boolean).join(" ")} {...rest}>
      <button
        ref={triggerRef}
        type="button"
        className={"gk-workspace__card" + (isOpen ? " gk-workspace__card--open" : "")}
        aria-haspopup={menu ? "menu" : undefined}
        aria-expanded={menu || open !== undefined ? isOpen : undefined}
        aria-controls={menu && menuOpen ? menuId : undefined}
        onClick={() => {
          if (menu) setMenuOpen((o) => !o);
          onClick?.();
        }}
        onKeyDown={(e) => {
          if (menu && e.key === "ArrowDown" && !menuOpen) {
            e.preventDefault();
            setMenuOpen(true);
          }
        }}
      >
        {initial && (
          <span className="gk-workspace__initial" aria-hidden="true">
            {initial}
          </span>
        )}
        <span className="gk-workspace__text">
          <span className="gk-workspace__row">
            <span className="gk-workspace__name">{name}</span>
            <Icon name="caret-down" size={16} className="gk-workspace__caret" />
          </span>
          {meta && <span className="gk-workspace__meta">{meta}</span>}
        </span>
      </button>
      {menu && menuOpen && (
        <div ref={menuRef} id={menuId} role="menu" aria-label={name} className="gk-workspace__menu" onKeyDown={onMenuKeyDown}>
          {menu.map((item, i) =>
            item.divider ? (
              <div key={"divider-" + i} role="separator" className="gk-workspace__sep" />
            ) : (
              <button
                key={item.id}
                type="button"
                data-menu-item=""
                role={item.checked === undefined ? "menuitem" : "menuitemradio"}
                aria-checked={item.checked === undefined ? undefined : item.checked}
                tabIndex={-1}
                className={"gk-workspace__item" + (item.checked ? " gk-workspace__item--checked" : "")}
                onClick={() => {
                  close(true);
                  onMenuSelect?.(item.id);
                }}
              >
                {item.icon && <Icon name={item.icon} size={18} />}
                <span className="gk-workspace__itemtext">
                  <span className="gk-workspace__itemlabel">{item.label}</span>
                  {item.meta && <span className="gk-workspace__itemmeta">{item.meta}</span>}
                </span>
                {item.checked && <Icon name="check" size={16} />}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
